import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTablePrimaryCell } from '@/components/data-display/data-table-primary-cell'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Select } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { ActionMenu } from '@/components/overlays/action-menu'
import { ConfirmationDialog } from '@/components/overlays/confirmation-dialog'
import { Drawer } from '@/components/overlays/drawer'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Button } from '@/components/primitives/button'
import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import { cn } from '@/lib/cn'

import type {
  OidcClaimSource,
  OidcClaimValueType,
  OidcProtocolMapper,
  OidcProtocolMapperPayload,
  OidcProtocolMapperType,
} from './api'
import { OidcClaimSourceSelect } from './idp-claim-source-select'

interface MapperDraftValues {
  addToIdToken: boolean
  addToIntrospection: boolean
  addToUserinfo: boolean
  claimJsonType: OidcClaimValueType
  enabled: boolean
  hardcodedValue: string
  mapperType: OidcProtocolMapperType
  name: string
  source: string
  tokenClaimName: string
}

interface MapperDraft {
  autoGeneratedClaimName: boolean
  autoGeneratedName: boolean
  mapperId: string | null
  mode: 'create' | 'edit'
  submitted: boolean
  values: MapperDraftValues
}

type OutputPreset = 'all' | 'custom' | 'mcp' | 'oidc'

function displayedHardcodedValue(value: unknown) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return value.toString()
  return JSON.stringify(value)
}

function initialValues(mapper?: OidcProtocolMapper): MapperDraftValues {
  return {
    addToIdToken: mapper?.add_to_id_token ?? true,
    addToIntrospection: mapper?.add_to_introspection ?? false,
    addToUserinfo: mapper?.add_to_userinfo ?? true,
    claimJsonType: mapper?.claim_json_type ?? 'string',
    enabled: mapper?.enabled ?? true,
    hardcodedValue: displayedHardcodedValue(mapper?.hardcoded_value),
    mapperType: mapper?.mapper_type ?? 'identity_source',
    name: mapper?.name ?? '',
    source: mapper?.source ?? '',
    tokenClaimName: mapper?.token_claim_name ?? '',
  }
}

function normalizeHardcodedValue(value: string, valueType: OidcClaimValueType): unknown {
  if (valueType === 'boolean') {
    if (value === 'true') return true
    if (value === 'false') return false
    throw new Error('Boolean hardcoded values must be true or false')
  }
  if (valueType === 'number') return Number(value)
  if (valueType === 'string_array') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  return value
}

function mapperPayload(values: MapperDraftValues): OidcProtocolMapperPayload {
  return {
    add_to_id_token: values.addToIdToken,
    add_to_introspection: values.addToIntrospection,
    add_to_userinfo: values.addToUserinfo,
    claim_json_type: values.claimJsonType,
    enabled: values.enabled,
    hardcoded_value:
      values.mapperType === 'hardcoded_claim'
        ? normalizeHardcodedValue(values.hardcodedValue, values.claimJsonType)
        : null,
    mapper_type: values.mapperType,
    name: values.name.trim(),
    source: values.mapperType === 'identity_source' ? values.source : null,
    token_claim_name: values.tokenClaimName.trim(),
  }
}

function isValid(values: MapperDraftValues) {
  const hardcodedIsValid =
    values.hardcodedValue.trim().length > 0 &&
    (values.claimJsonType !== 'number' || Number.isFinite(Number(values.hardcodedValue))) &&
    (values.claimJsonType !== 'boolean' || ['true', 'false'].includes(values.hardcodedValue))

  return Boolean(
    values.name.trim() &&
    values.tokenClaimName.trim() &&
    (values.mapperType === 'identity_source' ? values.source : hardcodedIsValid) &&
    (values.addToIdToken || values.addToUserinfo || values.addToIntrospection),
  )
}

function formatSource(mapper: OidcProtocolMapper) {
  if (mapper.source) return mapper.source
  return displayedHardcodedValue(mapper.hardcoded_value)
}

function suggestedClaimName(source: string) {
  const sourceKey = source.split('.').at(-1) ?? ''
  const normalized = sourceKey.replace(/[^A-Za-z0-9_.:-]+/g, '_')
  return /^[A-Za-z]/.test(normalized) ? normalized : `claim_${normalized}`
}

function outputPreset(values: MapperDraftValues): OutputPreset {
  if (values.addToIdToken && values.addToUserinfo && values.addToIntrospection) return 'all'
  if (values.addToIdToken && values.addToUserinfo && !values.addToIntrospection) return 'oidc'
  if (!values.addToIdToken && !values.addToUserinfo && values.addToIntrospection) return 'mcp'
  return 'custom'
}

function previewValue(values: MapperDraftValues): unknown {
  if (values.mapperType === 'hardcoded_claim' && values.hardcodedValue.trim()) {
    try {
      return normalizeHardcodedValue(values.hardcodedValue, values.claimJsonType)
    } catch {
      return null
    }
  }
  if (values.claimJsonType === 'string_array') return ['<string>']
  if (values.claimJsonType === 'boolean') return true
  if (values.claimJsonType === 'number') return 0
  return '<string>'
}

function DestinationSwitch({
  checked,
  description,
  disabled,
  label,
  onCheckedChange,
}: {
  checked: boolean
  description: string
  disabled?: boolean
  label: string
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start justify-between gap-4 py-3">
      <span>
        <span className="block text-sm font-medium text-[var(--color-text)]">{label}</span>
        <span className="mt-0.5 block text-xs leading-5 text-[var(--color-text-muted)]">
          {description}
        </span>
      </span>
      <Switch
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </label>
  )
}

function MapperHelp({ text }: { text: string }) {
  return (
    <HelpTooltip label={text} widthClassName="w-[280px]">
      {text}
    </HelpTooltip>
  )
}

export function OidcProtocolMapperTable({
  deleting,
  mappers,
  onCreate,
  onDelete,
  onUpdate,
  readOnly,
  saving,
  sources,
}: {
  deleting: boolean
  mappers: OidcProtocolMapper[]
  onCreate: (payload: OidcProtocolMapperPayload) => Promise<unknown>
  onDelete: (mapper: OidcProtocolMapper) => void
  onUpdate: (mapperId: string, payload: OidcProtocolMapperPayload) => Promise<unknown>
  readOnly: boolean
  saving: boolean
  sources: OidcClaimSource[]
}) {
  const { t } = useTranslation('adminIdp')
  const [draft, setDraft] = useState<MapperDraft | null>(null)
  const [pendingDeleteMapper, setPendingDeleteMapper] = useState<OidcProtocolMapper | null>(null)
  const mapperTypeOptions = [
    { label: t('mappers.types.identitySource'), value: 'identity_source' },
    { label: t('mappers.types.hardcodedClaim'), value: 'hardcoded_claim' },
  ]
  const valueTypeOptions = (['string', 'string_array', 'boolean', 'number'] as const).map(
    (value) => ({ label: t(`mappers.valueTypes.${value}`), value }),
  )
  const outputPresetOptions = (['oidc', 'mcp', 'all', 'custom'] as const).map((value) => ({
    label: t(`mappers.outputPresets.${value}`),
    value,
  }))

  const updateDraft = (values: Partial<MapperDraftValues>, metadata?: Partial<MapperDraft>) =>
    setDraft((current) =>
      current ? { ...current, ...metadata, values: { ...current.values, ...values } } : current,
    )

  const beginCreate = () =>
    setDraft({
      autoGeneratedClaimName: true,
      autoGeneratedName: true,
      mapperId: null,
      mode: 'create',
      submitted: false,
      values: initialValues(),
    })

  const beginEdit = (mapper: OidcProtocolMapper) =>
    setDraft({
      autoGeneratedClaimName: false,
      autoGeneratedName: false,
      mapperId: mapper.id,
      mode: 'edit',
      submitted: false,
      values: initialValues(mapper),
    })

  const saveDraft = async () => {
    if (!draft) return
    if (!isValid(draft.values)) {
      setDraft({ ...draft, submitted: true })
      return
    }

    try {
      if (draft.mode === 'create') {
        await onCreate(mapperPayload(draft.values))
      } else if (draft.mapperId) {
        await onUpdate(draft.mapperId, mapperPayload(draft.values))
      }
      setDraft(null)
    } catch {
      // mutation 的错误反馈由页面统一处理，保留表单方便管理员修正后重试。
    }
  }

  const selectedSource = draft
    ? sources.find((source) => source.source === draft.values.source)
    : undefined
  const invalid = Boolean(draft?.submitted && !isValid(draft.values))
  const preview = draft?.values.tokenClaimName.trim()
    ? JSON.stringify({ [draft.values.tokenClaimName.trim()]: previewValue(draft.values) }, null, 2)
    : '{}'
  const columns: DataTableColumn<OidcProtocolMapper>[] = [
    {
      header: t('mappers.columns.name'),
      id: 'name',
      render: (mapper) => (
        <DataTablePrimaryCell
          subtitle={t(`mappers.types.${mapper.mapper_type}`)}
          title={
            <span className="flex items-center gap-2">
              <span className="min-w-0 truncate">{mapper.name}</span>
              <Badge className="shrink-0" variant={mapper.enabled ? 'success' : 'neutral'}>
                {mapper.enabled ? t('status.enabled') : t('status.disabled')}
              </Badge>
            </span>
          }
        />
      ),
      width: 240,
    },
    {
      header: t('mappers.columns.mappingSource'),
      id: 'mapping-source',
      render: (mapper) => (
        <code
          className="block max-w-[320px] truncate text-xs text-[var(--color-text-muted)]"
          title={formatSource(mapper)}
        >
          {formatSource(mapper)}
        </code>
      ),
      width: 340,
    },
    {
      header: t('mappers.columns.claimOutput'),
      id: 'claim-output',
      render: (mapper) => (
        <DataTablePrimaryCell
          subtitle={t(`mappers.valueTypes.${mapper.claim_json_type}`)}
          title={<code className="text-xs">{mapper.token_claim_name}</code>}
        />
      ),
      width: 220,
    },
    {
      header: t('mappers.columns.destinations'),
      id: 'destinations',
      render: (mapper) => (
        <span className="flex min-h-[88px] flex-col items-start justify-center gap-1.5 py-2">
          {mapper.add_to_id_token ? <Badge variant="neutral">ID Token</Badge> : null}
          {mapper.add_to_userinfo ? <Badge variant="neutral">UserInfo</Badge> : null}
          {mapper.add_to_introspection ? <Badge variant="info">Introspection</Badge> : null}
        </span>
      ),
      width: 260,
    },
    {
      align: 'right',
      header: t('clientScopes.columns.actions'),
      hidden: readOnly,
      id: 'actions',
      pin: 'right',
      render: (mapper) => (
        <ActionMenu
          ariaLabel={t('mappers.rowActions.open', { name: mapper.name })}
          items={[
            {
              disabled: draft !== null,
              iconSrc: actionEditIconUrl,
              key: 'edit',
              label: t('actions.edit'),
              onSelect: () => beginEdit(mapper),
            },
            {
              disabled: draft !== null || deleting,
              iconSrc: actionDeleteIconUrl,
              key: 'delete',
              label: t('actions.delete'),
              onSelect: () => setPendingDeleteMapper(mapper),
              variant: 'danger',
            },
          ]}
        />
      ),
      width: 90,
    },
  ]

  return (
    <>
      <div className="grid gap-5">
        <div className="flex items-end justify-between gap-6">
          <div>
            <h2 className="text-base font-semibold">{t('mappers.title')}</h2>
            <p className="mt-1 max-w-[760px] text-sm leading-6 text-[var(--color-text-muted)]">
              {readOnly ? t('mappers.readOnlyDescription') : t('mappers.description')}
            </p>
          </div>
          {readOnly ? null : (
            <Button disabled={draft !== null} onClick={beginCreate}>
              {t('mappers.create')}
            </Button>
          )}
        </div>

        <DataTable
          columns={columns}
          empty={
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-sm font-medium">{t('mappers.empty')}</p>
              <p className="mt-1 max-w-[480px] text-sm leading-6 text-[var(--color-text-muted)]">
                {t('mappers.emptyHint')}
              </p>
              {readOnly ? null : (
                <Button
                  className="mt-4"
                  disabled={draft !== null}
                  onClick={beginCreate}
                  variant="secondary"
                >
                  {t('mappers.create')}
                </Button>
              )}
            </div>
          }
          getRowKey={(mapper) => mapper.id}
          minWidth={1150}
          rows={mappers}
        />
      </div>
      <Drawer
        bodyClassName="overflow-y-auto bg-[var(--color-surface-muted)] px-6 py-6"
        footer={
          draft ? (
            <>
              <p
                className={cn(
                  'mr-auto text-sm',
                  invalid ? 'text-[var(--color-danger)]' : 'text-transparent',
                )}
                role={invalid ? 'alert' : undefined}
              >
                {invalid ? t('mappers.validation.required') : '—'}
              </p>
              <Button disabled={saving} onClick={() => setDraft(null)} variant="secondary">
                {t('actions.cancel')}
              </Button>
              <Button isLoading={saving} onClick={() => void saveDraft()}>
                {t('actions.save')}
              </Button>
            </>
          ) : null
        }
        onOpenChange={(open) => {
          if (!open && !saving) setDraft(null)
        }}
        open={draft !== null}
        size="lg"
        title={draft?.mode === 'edit' ? t('mappers.editTitle') : t('mappers.createTitle')}
        titleMeta={
          draft ? (
            <span>{draft.values.enabled ? t('status.enabled') : t('status.disabled')}</span>
          ) : undefined
        }
      >
        {draft ? (
          <div className="grid gap-5">
            <div className="grid items-start gap-5">
              <div className="contents">
                <section className="grid gap-5 rounded-xl border border-[var(--color-panel-border)] bg-white p-5">
                  <div>
                    <h3 className="font-semibold">{t('mappers.sections.mapping')}</h3>
                    <p className="mt-1 text-sm leading-6 text-[var(--color-text-muted)]">
                      {t('mappers.sections.mappingDescription')}
                    </p>
                  </div>
                  <FormField
                    htmlFor="mapper-type"
                    label={t('mappers.fields.type')}
                    labelExtra={<MapperHelp text={t('mappers.hints.type')} />}
                    required
                  >
                    <Select
                      id="mapper-type"
                      onValueChange={(value) =>
                        updateDraft({ mapperType: value as OidcProtocolMapperType })
                      }
                      options={mapperTypeOptions}
                      value={draft.values.mapperType}
                    />
                  </FormField>

                  {draft.values.mapperType === 'identity_source' ? (
                    <FormField
                      htmlFor="mapper-source"
                      label={t('mappers.fields.source')}
                      labelExtra={<MapperHelp text={t('mappers.hints.source')} />}
                      required
                    >
                      <OidcClaimSourceSelect
                        id="mapper-source"
                        invalid={invalid && !draft.values.source}
                        onValueChange={(source) => {
                          const selected = sources.find((item) => item.source === source)
                          updateDraft({
                            claimJsonType: selected?.value_type ?? draft.values.claimJsonType,
                            name: draft.autoGeneratedName
                              ? selected
                                ? t(`mappers.claimSources.${selected.key}`, {
                                    defaultValue: selected.label,
                                  })
                                : ''
                              : draft.values.name,
                            source,
                            tokenClaimName: draft.autoGeneratedClaimName
                              ? suggestedClaimName(source)
                              : draft.values.tokenClaimName,
                          })
                        }}
                        sources={sources}
                        value={draft.values.source}
                      />
                      {selectedSource ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-[var(--color-surface-muted)] px-3 py-2 text-xs">
                          <code className="min-w-0 break-all">{selectedSource.source}</code>
                          <Badge variant="neutral">
                            {t(`mappers.origins.${selectedSource.origin}`)}
                          </Badge>
                          <Badge variant="neutral">
                            {t(`mappers.valueTypes.${selectedSource.value_type}`)}
                          </Badge>
                        </div>
                      ) : null}
                    </FormField>
                  ) : (
                    <>
                      <FormField
                        htmlFor="mapper-hardcoded-value"
                        label={t('mappers.fields.hardcodedValue')}
                        labelExtra={<MapperHelp text={t('mappers.hints.hardcodedValue')} />}
                        required
                      >
                        {draft.values.claimJsonType === 'boolean' ? (
                          <Select
                            id="mapper-hardcoded-value"
                            onValueChange={(hardcodedValue) => updateDraft({ hardcodedValue })}
                            options={[
                              { label: 'true', value: 'true' },
                              { label: 'false', value: 'false' },
                            ]}
                            value={
                              ['true', 'false'].includes(draft.values.hardcodedValue)
                                ? draft.values.hardcodedValue
                                : 'false'
                            }
                          />
                        ) : (
                          <Input
                            id="mapper-hardcoded-value"
                            invalid={invalid && !draft.values.hardcodedValue.trim()}
                            onChange={(event) =>
                              updateDraft({ hardcodedValue: event.target.value })
                            }
                            placeholder={t('mappers.placeholders.hardcodedValue')}
                            value={draft.values.hardcodedValue}
                          />
                        )}
                      </FormField>
                      <FormField
                        htmlFor="mapper-hardcoded-type"
                        label={t('mappers.fields.claimJsonType')}
                        labelExtra={<MapperHelp text={t('mappers.hints.claimJsonType')} />}
                        required
                      >
                        <Select
                          id="mapper-hardcoded-type"
                          onValueChange={(value) => {
                            const claimJsonType = value as OidcClaimValueType
                            updateDraft({
                              claimJsonType,
                              ...(claimJsonType === 'boolean' &&
                              !['true', 'false'].includes(draft.values.hardcodedValue)
                                ? { hardcodedValue: 'false' }
                                : {}),
                            })
                          }}
                          options={valueTypeOptions}
                          value={draft.values.claimJsonType}
                        />
                      </FormField>
                    </>
                  )}

                  <FormField
                    htmlFor="mapper-name"
                    label={t('mappers.fields.name')}
                    labelExtra={<MapperHelp text={t('mappers.hints.name')} />}
                    required
                  >
                    <Input
                      id="mapper-name"
                      invalid={invalid && !draft.values.name.trim()}
                      onChange={(event) =>
                        updateDraft({ name: event.target.value }, { autoGeneratedName: false })
                      }
                      placeholder={t('mappers.placeholders.name')}
                      value={draft.values.name}
                    />
                  </FormField>
                  <FormField
                    htmlFor="mapper-claim-name"
                    label={t('mappers.fields.tokenClaimName')}
                    labelExtra={<MapperHelp text={t('mappers.hints.tokenClaimName')} />}
                    required
                  >
                    <Input
                      className="font-mono"
                      id="mapper-claim-name"
                      invalid={invalid && !draft.values.tokenClaimName.trim()}
                      onChange={(event) =>
                        updateDraft(
                          { tokenClaimName: event.target.value },
                          { autoGeneratedClaimName: false },
                        )
                      }
                      placeholder={t('mappers.placeholders.tokenClaimName')}
                      value={draft.values.tokenClaimName}
                    />
                  </FormField>
                  {draft.values.mapperType === 'identity_source' ? (
                    <FormField
                      htmlFor="mapper-value-type"
                      label={t('mappers.fields.claimJsonType')}
                      labelExtra={<MapperHelp text={t('mappers.hints.autoValueType')} />}
                    >
                      <Select
                        disabled
                        id="mapper-value-type"
                        onValueChange={() => undefined}
                        options={valueTypeOptions}
                        value={draft.values.claimJsonType}
                      />
                    </FormField>
                  ) : null}
                </section>

                <section className="rounded-xl border border-[var(--color-panel-border)] bg-white p-5">
                  <div>
                    <h3 className="font-semibold">{t('mappers.sections.destinations')}</h3>
                    <p className="mt-1 text-sm leading-6 text-[var(--color-text-muted)]">
                      {t('mappers.sections.destinationsDescription')}
                    </p>
                  </div>
                  <div className="mt-5">
                    <FormField
                      htmlFor="mapper-output-preset"
                      label={t('mappers.fields.outputPreset')}
                      labelExtra={<MapperHelp text={t('mappers.hints.outputPreset')} />}
                    >
                      <Select
                        id="mapper-output-preset"
                        onValueChange={(value) => {
                          const preset = value as OutputPreset
                          if (preset === 'custom') return
                          updateDraft({
                            addToIdToken: preset === 'oidc' || preset === 'all',
                            addToIntrospection: preset === 'mcp' || preset === 'all',
                            addToUserinfo: preset === 'oidc' || preset === 'all',
                          })
                        }}
                        options={outputPresetOptions}
                        value={outputPreset(draft.values)}
                      />
                    </FormField>
                  </div>
                  <div
                    className={cn(
                      'mt-2 divide-y divide-[var(--color-divider)]',
                      invalid &&
                        !draft.values.addToIdToken &&
                        !draft.values.addToUserinfo &&
                        !draft.values.addToIntrospection
                        ? 'rounded-lg ring-1 ring-[var(--color-danger)]'
                        : null,
                    )}
                  >
                    <DestinationSwitch
                      checked={draft.values.addToIdToken}
                      description={t('mappers.destinations.idToken')}
                      label="ID Token"
                      onCheckedChange={(addToIdToken) => updateDraft({ addToIdToken })}
                    />
                    <DestinationSwitch
                      checked={draft.values.addToUserinfo}
                      description={t('mappers.destinations.userinfo')}
                      label="UserInfo"
                      onCheckedChange={(addToUserinfo) => updateDraft({ addToUserinfo })}
                    />
                    <DestinationSwitch
                      checked={draft.values.addToIntrospection}
                      description={t('mappers.destinations.introspection')}
                      label="Introspection"
                      onCheckedChange={(addToIntrospection) => updateDraft({ addToIntrospection })}
                    />
                  </div>
                </section>
              </div>

              <section className="grid gap-4 rounded-xl border border-[var(--color-panel-border)] bg-white p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold">{t('mappers.sections.behavior')}</h3>
                    <p className="mt-1 text-sm leading-6 text-[var(--color-text-muted)]">
                      {t('mappers.sections.behaviorDescription')}
                    </p>
                  </div>
                  <Switch
                    aria-label={t('mappers.fields.enabled')}
                    checked={draft.values.enabled}
                    onCheckedChange={(enabled) => updateDraft({ enabled })}
                  />
                </div>
                <section className="rounded-xl bg-[#111827] p-4 text-white">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold">{t('mappers.preview.title')}</h4>
                    <Badge variant="neutral">JSON</Badge>
                  </div>
                  <pre className="mt-3 overflow-x-auto text-xs leading-5 text-[#dbeafe]">
                    {preview}
                  </pre>
                  <p className="mt-3 text-xs leading-5 text-[#9ca3af]">
                    {t('mappers.preview.description')}
                  </p>
                </section>
              </section>
            </div>
          </div>
        ) : null}
      </Drawer>
      <ConfirmationDialog
        cancelLabel={t('actions.cancel')}
        confirmLabel={t('actions.delete')}
        description={t('mappers.confirmDelete', { name: pendingDeleteMapper?.name ?? '' })}
        isConfirming={deleting}
        onCancel={() => setPendingDeleteMapper(null)}
        onConfirm={() => {
          if (!pendingDeleteMapper) return
          const mapper = pendingDeleteMapper
          setPendingDeleteMapper(null)
          onDelete(mapper)
        }}
        open={Boolean(pendingDeleteMapper)}
        title={t('mappers.confirmDeleteTitle')}
        tone="danger"
      />
    </>
  )
}
